import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Grid,
  Divider,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SchoolIcon from "@mui/icons-material/School";
import DescriptionIcon from "@mui/icons-material/Description";
import CategoryIcon from "@mui/icons-material/Category";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DiscountIcon from "@mui/icons-material/Discount";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getCourseTypes } from "../../State/Enum/Action";
import { addCourse } from "../../State/Course/Action";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const Course = () => {
  const { enums, course } = useSelector((store) => store);
  const [courseType, setCourseType] = useState("");
  const [file, setFile] = useState(null);
  const [courseName, setCourseName] = useState("");
  const [courseDescription, setCourseDescription] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getCourseTypes());
  }, [dispatch]);

  const handleChange = (event) => {
    setCourseType(event.target.value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const courseData = {
      name: data.get("name"),
      description: data.get("description"),
      type: data.get("type"),
      price: data.get("price"),
      discountAmount: Number(discount) || 0,
      thumbnail: file,
    };
    const reqData = { courseData, navigate };
    dispatch(addCourse(reqData));
  };
  const [price, setPrice] = useState("");
  const [discount, setDiscount] = useState("");
  const finalPrice = Math.max(
    0,
    (Number(price) || 0) - (Number(discount) || 0)
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(to right, #667eea, #764ba2)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        py: 5,
        px: 2,
      }}
    >
      <Card
        sx={{
          width: "100%",
          maxWidth: 600,
          p: 3,
          borderRadius: 4,
          boxShadow: 6,
          backgroundColor: "#fff",
        }}
      >
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{
                mr: 2,
                backgroundColor: "rgba(102, 126, 234, 0.1)",
                "&:hover": {
                  backgroundColor: "rgba(102, 126, 234, 0.2)",
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography
              variant="h5"
              fontWeight="bold"
              color="primary"
              sx={{ display: "flex", alignItems: "center" }}
            >
              <SchoolIcon sx={{ mr: 1 }} />
              Add New Course
            </Typography>
          </Box>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="name"
                  variant="outlined"
                  label="Course Name"
                  value={courseName}
                  onChange={(e) => setCourseName(e.target.value)}
                  helperText={`${courseName.length}/1000 characters`}
                  inputProps={{
                    maxLength: 1000
                  }}
                  InputProps={{
                    startAdornment: <SchoolIcon sx={{ mr: 1 }} />,
                  }}
                  sx={{
                    '& .MuiFormHelperText-root': {
                      color: courseName.length > 800 ? '#f59e0b' : 
                             courseName.length > 900 ? '#ef4444' : '#64748b',
                      fontWeight: '500',
                      fontSize: '0.875rem',
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="description"
                  variant="outlined"
                  label="Description"
                  multiline
                  rows={3}
                  value={courseDescription}
                  onChange={(e) => setCourseDescription(e.target.value)}
                  helperText={`${courseDescription.length}/1000 characters`}
                  inputProps={{
                    maxLength: 1000
                  }}
                  InputProps={{
                    startAdornment: <DescriptionIcon sx={{ mr: 1 }} />,
                  }}
                  sx={{
                    '& .MuiFormHelperText-root': {
                      color: courseDescription.length > 800 ? '#f59e0b' : 
                             courseDescription.length > 900 ? '#ef4444' : '#64748b',
                      fontWeight: '500',
                      fontSize: '0.875rem',
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  fullWidth
                  component="label"
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                >
                  {file ? file.name : "Upload Thumbnail"}
                  <VisuallyHiddenInput
                    type="file"
                    onChange={(e) => setFile(e.target.files[0])}
                  />
                </Button>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>
                    <CategoryIcon sx={{ mr: 1 }} />
                    Course Type
                  </InputLabel>
                  <Select
                    value={courseType}
                    onChange={handleChange}
                    name="type"
                    label="Course Type"
                  >
                    {enums.courseTypes?.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  type="number"
                  name="price"
                  fullWidth
                  variant="outlined"
                  label="Course Price"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  InputProps={{
                    startAdornment: <CurrencyRupeeIcon sx={{ mr: 1 }} />,
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  type="number"
                  name="discountAmount"
                  fullWidth
                  variant="outlined"
                  label="Discount Amount"
                  value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                  InputProps={{
                    startAdornment: <DiscountIcon sx={{ mr: 1 }} />,
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  name="finalPrice"
                  fullWidth
                  variant="outlined"
                  label="Final Price"
                  value={finalPrice}
                  InputProps={{
                    startAdornment: <CurrencyRupeeIcon sx={{ mr: 1 }} />,
                    readOnly: true,
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  endIcon={<NavigateNextIcon />}
                  sx={{
                    fontWeight: "bold",
                    background: "linear-gradient(to right, #43cea2, #185a9d)",
                    color: "#fff",
                  }}
                >
                  Submit & Continue
                </Button>
              </Grid>

              {course?.error && (
                <Grid item xs={12}>
                  <Typography color="error">{course.error}</Typography>
                </Grid>
              )}
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Course;
